import {ModelContext} from '../../../types/app.types';
import {UrlModel} from './Url.model';
import {ImageModel} from './Image.model';
import {PriceModel} from '../Price.model';
import {DiscountModel} from './Discount.model';
import {DescriptionLineModel} from './DescriptionLine.model';
import {LineItemWithPrivateFields} from '../Cart/Cart.model';
import {DescriptionLine, PaymentOptionType as PaymentOptionTypeCartV1} from '@wix/auto_sdk_ecom_current-cart';
import {V2LineItem as LineItem} from '@wix/auto_sdk_ecom_current-cart-v-2';
import {ModifierGroupModel} from './ModifierGroup.model';
import {SubscriptionInfoModel} from './SubscriptionInfo.model';
import {AttributesModel} from './Attributes.model';
import {PaymentConfigModel} from './PaymentConfig.model';
import {SourceModel} from './Source.model';
import {PricingModel} from './Pricing.model';
import {QuantityInfoModel} from './QuantityInfo.model';

export class LineItemModel {
  public id: string;
  public name: string;
  public attributes: AttributesModel;
  public paymentConfig: PaymentConfigModel;
  public quantityInfo: QuantityInfoModel;
  public source: SourceModel;
  public pricing: PricingModel;
  public modifierGroups: ModifierGroupModel[];
  public discounts: DiscountModel[];

  constructor(params: LineItemModel) {
    this.id = params.id;
    this.name = params.name;
    this.attributes = params.attributes;
    this.paymentConfig = params.paymentConfig;
    this.quantityInfo = params.quantityInfo;
    this.source = params.source;
    this.pricing = params.pricing;
    this.modifierGroups = params.modifierGroups;
    this.discounts = params.discounts;
  }

  public static fromCartV1(
    lineItem: LineItemWithPrivateFields,
    discountRulesNames: string[] = [],
    modelContext: ModelContext
  ) {
    const crossedOutPrice = getCrossedOutPrice(lineItem);
    const modeledCrossedOutPrice = crossedOutPrice ? PriceModel.fromCartV1(crossedOutPrice, modelContext) : undefined;
    const modeledPrice = PriceModel.fromCartV1(lineItem?.price, modelContext);
    const modeledLineItemPrice = PriceModel.fromCartV1(lineItem.lineItemPrice, modelContext);
    const modeledModifiersTotalPrice = PriceModel.fromCartV1(lineItem.modifiersTotalPrice, modelContext);
    const quantity = lineItem.quantity;
    const itemType =
      lineItem.itemType?.preset?.toLowerCase() ?? /* istanbul ignore next */ lineItem.itemType?.custom?.toLowerCase();
    const url = UrlModel.fromCartV1(lineItem.url, modelContext);
    const image = lineItem?.image ? ImageModel.fromCartV1(lineItem.image) : /* istanbul ignore next */ undefined;
    const descriptionLines = (lineItem?.descriptionLines ?? /* istanbul ignore next */ []).map(
      (descriptionLine: DescriptionLine) => DescriptionLineModel.fromCartV1(descriptionLine)
    );
    const subscriptionInfo = SubscriptionInfoModel.fromCartV1(lineItem.subscriptionOptionInfo);
    const depositAmount = lineItem?.depositAmount
      ? PriceModel.fromCartV1(lineItem.depositAmount, modelContext)
      : undefined;
    return new LineItemModel({
      // eslint-disable-next-line import/no-deprecated
      id: lineItem._id,
      name: lineItem.productName.translated,
      attributes: AttributesModel.fromCartV1({
        shippable: lineItem.physicalProperties?.shippable,
        itemType,
        url,
        image,
        descriptionLines,
        subscriptionInfo,
      }),
      paymentConfig: PaymentConfigModel.fromCartV1({
        savePaymentMethod: lineItem.savePaymentMethod,
        depositAmount,
        paymentOption: lineItem.paymentOption as PaymentOptionTypeCartV1,
      }),
      quantityInfo: QuantityInfoModel.fromCartV1({
        quantity,
        availableQuantity: lineItem.availability?.quantityAvailable,
        fixedQuantity: lineItem.fixedQuantity,
      }),
      source: SourceModel.fromCartV1(lineItem?.catalogReference),
      pricing: PricingModel.fromCartV1({
        unitPrice: modeledPrice,
        totalPrice: modeledLineItemPrice,
        priceUndetermined: lineItem.priceUndetermined,
        comparePrice: modeledCrossedOutPrice,
        comparePriceByQuantity: crossedOutPrice
          ? PriceModel.fromCartV1(
              crossedOutPriceByQuantity({
                modeledCrossedOutPrice,
                quantity,
                modelContext,
              }),
              modelContext
            )
          : undefined,
        basePrice: PriceModel.fromCartV1(
          getBaseUnitPrice({
            modeledUnitPrice: modeledPrice,
            modeledCrossedOutPrice,
            modeledTotalModifiersPrice: modeledModifiersTotalPrice,
            modelContext,
          }),
          modelContext
        ),
        totalModifiersPrice: modeledModifiersTotalPrice,
      }),
      modifierGroups: (lineItem.modifierGroups ?? []).map((modifierGroup) =>
        ModifierGroupModel.fromCartV1(modifierGroup, modelContext)
      ),
      discounts: discountRulesNames.map((name) => DiscountModel.fromCartV1(name)),
    });
  }

  public static fromCartV2(lineItem: LineItem, modelContext: ModelContext): LineItemModel {
    return new LineItemModel({
      id: lineItem._id,
      name: lineItem.name.translated,
      attributes: AttributesModel.fromCartV2(lineItem.attributes, modelContext),
      paymentConfig: PaymentConfigModel.fromCartV2(lineItem.paymentConfig, modelContext),
      quantityInfo: QuantityInfoModel.fromCartV2(lineItem.quantityInfo),
      source: SourceModel.fromCartV2(lineItem.source),
      pricing: PricingModel.fromCartV2(lineItem.pricing, lineItem.quantityInfo.confirmedQuantity, modelContext),
      modifierGroups: (lineItem.modifierGroups ?? []).map((modifierGroup) =>
        ModifierGroupModel.fromCartV2(modifierGroup, modelContext)
      ),
      discounts: lineItem.discounts?.map((discount) => DiscountModel.fromCartV2(discount)),
    });
  }
}

function getCrossedOutPrice(lineItem: LineItemWithPrivateFields) {
  if (lineItem.priceBeforeDiscounts?.formattedConvertedAmount !== lineItem.price?.formattedConvertedAmount) {
    return lineItem.priceBeforeDiscounts;
  } else if (lineItem.fullPrice?.formattedConvertedAmount !== lineItem.price?.formattedConvertedAmount) {
    return lineItem.fullPrice;
  }
  return undefined;
}

export function getBaseUnitPrice({
  modeledUnitPrice,
  modeledCrossedOutPrice,
  modeledTotalModifiersPrice,
  modelContext,
}: {
  modeledUnitPrice: PriceModel;
  modeledCrossedOutPrice: PriceModel;
  modeledTotalModifiersPrice?: PriceModel;
  modelContext: ModelContext;
}) {
  if (modeledTotalModifiersPrice.convertedAmount === 0) {
    return {
      convertedAmount: modeledUnitPrice.convertedAmount.toString(),
      formattedConvertedAmount: modeledUnitPrice.formattedConvertedAmount,
    };
  }

  const unitPriceAmount = modeledCrossedOutPrice?.convertedAmount
    ? modeledCrossedOutPrice?.convertedAmount
    : modeledUnitPrice.convertedAmount;

  const baseUnitPriceAmount = unitPriceAmount - modeledTotalModifiersPrice.convertedAmount;

  return {
    convertedAmount: baseUnitPriceAmount.toString(),
    formattedConvertedAmount: modelContext.formatAmount(baseUnitPriceAmount),
  };
}

function crossedOutPriceByQuantity({
  modeledCrossedOutPrice,
  quantity,
  modelContext,
}: {
  modeledCrossedOutPrice: PriceModel;
  quantity: number;
  modelContext: ModelContext;
}) {
  return {
    convertedAmount: (modeledCrossedOutPrice.convertedAmount * quantity).toString(),
    formattedConvertedAmount: modelContext.formatAmount(modeledCrossedOutPrice.convertedAmount * quantity),
  };
}
